import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  fetchStoreFeed,
  selectConfig,
  selectDistributionMethod,
  selectError,
  selectIsLoading,
  selectItems,
  updateDistributionMethod
} from "../../redux/storeFeed/storeFeedSlice";
import {Container} from "../containers/Container";
import {Heading4, HeroHeading2} from "../text";
import React, {useEffect} from "react";
import {StoreFeedVendorCardView} from "./StoreFeedVendorCardView";
import {
  StoreColorIcon,
  StoreDeliveryUnavailableGraphic,
  StoreLocationGraphic,
  StorePickupUnavailableGraphic,
  WarningColorIcon
} from "../icons";
import {SegmentedControl, SegmentedControlItem} from "../segmentedControls/SegmentedControl";
import {DistributionMethod} from "../../models/distributionMethod";
import Skeleton from "react-loading-skeleton";
import {ProductModel} from "../../models/product";
import {StoreEmptyStateView} from "./StoreEmptyStateView";
import {useRouter} from "next/router";
import {fetchRecentCart} from "../../redux/cart/cartSlice";
import {NavBarNegativeHeight} from "../navBars/NavBarMain";
import {EventLogger} from "../../utils/eventLogger";
import {AnalyticsEvent} from "../../utils/eventProperties";

export const StoreFeedView: React.FC<any> = () => {
  const { t } = useTranslation()
  const router = useRouter();

  const dispatch = useAppDispatch();
  let items = useAppSelector(selectItems);
  const config = useAppSelector(selectConfig);
  const error = useAppSelector(selectError);
  const isLoading = useAppSelector(selectIsLoading);

  if (items.length === 0 && isLoading) {
    items = [0, 1, 2].map(i => {
      return {
        id: i.toString(),
        storeAttributes: [{ text: '1', id: 'id1' }, { text: '2', id: 'id2' }],
        totalItemCount: 17,
        products: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16].map(i => ProductModel.fromJson({_id: i.toString()})!)
      }
    })
  }

  const distributionMethod = useAppSelector(selectDistributionMethod);

  const segmentedControlItems: SegmentedControlItem<DistributionMethod>[] = [
    { value: 'delivery', id: 'delivery' },
    { value: 'pickup', id: 'pickup' },
  ]

  useEffect(() => {
    EventLogger.logEvent(new AnalyticsEvent({ eventName: 'Screen Visited', screenName: 'Store Feed' }))

    dispatch(fetchStoreFeed({ shouldAutoRoute: true }))
    dispatch(fetchRecentCart())
  }, [])

  if (error) {
    return <div className={`h-screen flex flex-col justify-center items-center ${NavBarNegativeHeight}`}>
      <StoreEmptyStateView
        onClick={() => dispatch(fetchStoreFeed({ shouldAutoRoute: true }))}
        icon={<WarningColorIcon />}
        title={error.localizedTitle}
        subtitle={error.localizedMessage}
        isLoading={isLoading}
        buttonTitle={t('store:store_empty_state_button_try_again')}
      />
    </div>
  }

  switch (config) {
    case "add_location":
      return <div className={`h-screen flex flex-col justify-center items-center ${NavBarNegativeHeight}`}>
        <StoreEmptyStateView
          onClick={() => {

          }}
          isLoading={isLoading}
          icon={<StoreLocationGraphic />}
          title={t('store:store_empty_state_title_where_are_you')}
          subtitle={t('store:store_empty_state_message_add_location')}
          buttonTitle={t('store:store_empty_state_button_add_location')}
        />
      </div>
    case "standard":
      break;
    case "no_stores_available":
      return <div className={`h-screen flex flex-col justify-center items-center ${NavBarNegativeHeight}`}>
        <StoreEmptyStateView
          onClick={() => {
            router.push(process.env.NEXT_PUBLIC_APP_DOWNLOAD_URL!)
          }}
          isLoading={isLoading}
          icon={<StoreColorIcon />}
          title={t('store:store_empty_state_title_coming_soon')}
          subtitle={t('store:store_empty_state_message_coming_soon')}
          buttonTitle={t('store:store_empty_state_button_download_now')}
        />
      </div>
    case "no_pickup_available":
      return <div className={`h-screen flex flex-col justify-center items-center ${NavBarNegativeHeight}`}>
        <StoreEmptyStateView
          onClick={() => {
            dispatch(updateDistributionMethod('delivery'))
          }}
          isLoading={isLoading}
          icon={<StorePickupUnavailableGraphic />}
          title={t('store:store_empty_state_title_pickup_unavailable')}
          subtitle={t('store:store_empty_state_message_pickup_unavailable')}
          buttonTitle={t('store:store_empty_state_button_switch_to_delivery')}
        />
      </div>
    case "no_delivery_available":
      return <div className={`h-screen flex flex-col justify-center items-center ${NavBarNegativeHeight}`}>
        <StoreEmptyStateView
          onClick={() => {
            dispatch(updateDistributionMethod('pickup'))
          }}
          isLoading={isLoading}
          icon={<StoreDeliveryUnavailableGraphic />}
          title={t('store:store_empty_state_title_delivery_unavailable')}
          subtitle={t('store:store_empty_state_message_delivery_unavailable')}
          buttonTitle={t('store:store_empty_state_button_switch_to_pickup')}
        />
      </div>
  }

  return <Container>
    <div className="flex space-x-4 items-center mb-4 lg:mb-6">
      <HeroHeading2 className="flex-grow">
        {
          isLoading ?
            <>
              <Skeleton className="!w-40 md:!w-1/3" />
            </> :
            t('store:store_title_stores_near_you')
        }
      </HeroHeading2>
      {isLoading ?
        <Skeleton className="!w-36 !h-9 !rounded-full !inline-flex md:!hidden" /> :
        <SegmentedControl
          className="block md:hidden flex-shrink-0"
          selectedItemId={distributionMethod}
          items={segmentedControlItems}
          renderItem={(item) => {
            return <Heading4 className="text-center capitalize">{item.value.toLowerCase()}</Heading4>
          }}
          onItemClick={(item) => dispatch(updateDistributionMethod(item.value))}
        />
      }
    </div>
    {items.map(item => {
      return <div key={item.id} className="mb-6">
        <StoreFeedVendorCardView store={item} isLoading={isLoading} />
      </div>
    })}
  </Container>
}